import React, { useMemo, useRef, useState } from 'react';
import { Badge, Icon, MoreActions, Text } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import type { IMoreActionItem } from '@ui-library/MoreActions/MoreActions.types';
import { DevProject } from '../../types/chat';
import { IfHasPermission } from '../IfHasPermission';
import { Permissions } from '../../constants';
import { EnvironmentRuntime, ProjectEnvironment } from '../../services/projectEnvironment';
import { getRelativeTime } from '../../utils/dateUtils';
import { useIsTruncated } from '../../hooks/useIsTruncated';
import {
  EnvironmentGroup,
  displayEnvironmentName,
  forkName,
  groupEnvironments,
  isBusyStatus,
  isDeployed,
  needsAttention,
  pipelineSummary,
  pipelineTone,
  runtimeAction,
  urlLabel,
} from './types';
import { ChainNode, EnvironmentBadge } from './StatusBadge';

interface RowHandlers {
  runtime: Record<string, EnvironmentRuntime>;
  runtimeActionId: string | null;
  /** Every id currently being deleted; several deletes may overlap. */
  deletingIds: string[];
  onStartStop: (environment: ProjectEnvironment, action: 'start' | 'stop') => void;
  onEdit: (environment: ProjectEnvironment) => void;
  onDelete: (environment: ProjectEnvironment) => void;
  onOpenDetails: (environment: ProjectEnvironment) => void;
  project: DevProject | null;
  /** Every environment of the project — the position labels name their source. */
  all: ProjectEnvironment[];
}

/**
 * One environment, as three lines: name and status, address, position.
 *
 * The same row serves a main and a sub — a sub is not a lesser thing, it just
 * sits inside its main's card, on a tinted tray. ``nested`` carries only what
 * that tray changes: which hover colour actually reads as a change.
 *
 * Actions live behind a dot-menu that appears on hover. Three icons per row was
 * nine at rest on a panel whose primary gesture is "click through", and the
 * menu costs nothing until it is wanted.
 */
const EnvironmentRow: React.FC<
  RowHandlers & {
    environment: ProjectEnvironment;
    nested?: boolean;
    /** Sits immediately after the name — the collapsed count, and nothing else
     *  so far. Passed in rather than derived here, because only the card knows
     *  whether it is collapsed. */
    trailing?: React.ReactNode;
  }
> = ({
  environment,
  nested = false,
  trailing,
  runtime,
  runtimeActionId,
  deletingIds,
  onStartStop,
  onEdit,
  onDelete,
  onOpenDetails,
  project,
  all,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const busy = isBusyStatus(environment.status);
  const deployed = isDeployed(environment.status);
  const deleting = deletingIds.includes(environment.id);
  const envRuntime = runtime[environment.id];
  const action = deployed && !busy ? runtimeAction(envRuntime, environment.status) : null;
  const actionPending = runtimeActionId === environment.id || envRuntime?.in_progress === true;

  const address = environment.url ? urlLabel(environment.url) : null;
  // The source is named once above the rows — in the panel subtitle for a
  // main, in the tray caption for a sub — so each line carries only this
  // environment's own checkpoint and its own distance.
  const summary = pipelineSummary(environment, all);
  const diverged = environment.pipeline_state === 'diverged';
  const fork = diverged ? forkName(environment) : null;

  // Truncated, so the row stays one line and the cards align down the column —
  // but a hidden tail needs a way back. Measured rather than assumed: the
  // tooltip appears only when an ellipsis is actually showing, so it never
  // repeats a line you can already read.
  const [positionRef, positionClipped] = useIsTruncated<HTMLSpanElement>(summary);
  const positionTitle =
    [positionClipped ? summary : null, fork ? `Split at ${fork}` : null]
      .filter(Boolean)
      .join('\n') || undefined;

  // Start/stop is operational and an admin may do it; editing and deleting
  // change what the environment *is*, and they may not. The gate around the
  // menu cannot express that — the distinction is between the items inside it
  // — so the trigger opens for either, and the list is filtered here.
  const canEdit = !!project?.permissions?.includes(Permissions.UpdateProjectPermission);

  const items: IMoreActionItem[] = [
    ...(deployed && action
      ? [{
          id: action,
          label: action === 'start' ? 'Start' : 'Stop',
          disabled: actionPending,
          onClick: () => onStartStop(environment, action),
        }]
      : []),
    ...(canEdit
      ? [
          {
            id: 'edit',
            label: 'Edit',
            disabled: busy || deleting,
            onClick: () => onEdit(environment),
          },
          {
            id: 'delete',
            label: 'Delete',
            variant: 'destructive' as const,
            disabled: busy || deleting,
            onClick: () => onDelete(environment),
          },
        ]
      : []),
  ];

  return (
    <div
      onClick={() => !deleting && onOpenDetails(environment)}
      className={`group flex flex-col gap-bg-v2-xxs px-bg-v2-lg py-bg-v2-md rounded-bg-v2-sm transition-colors ${
        // Nothing to open and nothing to act on once it is going.
        deleting ? 'opacity-60 pointer-events-none' : 'cursor-pointer'
      } ${
        // A nested row already sits on the tinted tray, so it needs the step
        // *above* that to register as hover — the main's hover colour is the
        // tray's own colour and would not move at all.
        nested ? 'hover:bg-bg-secondary-hover' : 'hover:bg-bg-secondary'
      }`}
    >
      <div className="flex items-center gap-bg-v2-xs w-full">
        {/* No glyph beside the name. A row inside a pipeline carries its status
            on the connector, where it reads as a station on the line; a head
            has no line, and its pill already says the same thing. */}
        <Text
          variant="text-sm"
          weight="semibold"
          color="primary"
          className="flex-1 min-w-0 truncate ml-bg-v2-xxs"
        >
          {displayEnvironmentName(environment.environment_name)}
        </Text>
        {trailing}

        <IfHasPermission
          permissions={project?.permissions}
          requires={Permissions.UpdateProjectPermission}
          allowAdmin
        >
          {/* Nothing to offer, so no trigger. Reachable now that the items are
              filtered: an admin looking at a never-deployed environment has no
              start/stop and no edit, and an empty menu is worse than none. */}
          {items.length > 0 && (
            <>
          {/* Held open while its menu is, so the trigger cannot vanish from
              under the pointer that opened it. */}
          <button
            ref={triggerRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((open) => !open);
            }}
            title="More actions"
            className={`flex items-center justify-center w-[24px] h-[24px] shrink-0 rounded-full hover:bg-bg-secondary-hover transition-opacity ${
              menuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'
            }`}
          >
            <Icon
              name={deleting ? IconName.Loading : IconName.More}
              size="xs"
              spin={deleting}
              className="text-text-tertiary"
              inheritColor
            />
          </button>
          <MoreActions
            items={items}
            isOpen={menuOpen}
            onClose={() => setMenuOpen(false)}
            position="bottom-right"
            triggerRef={triggerRef}
            width={150}
          />
            </>
          )}
        </IfHasPermission>

        {/* Always, every row. Hiding it for a healthy environment left the
            column with a pill for Stopped and Starting and a blank for the
            normal case, which reads as missing data rather than as quiet. The
            dot is the glyph you scan; the pill is the word you read. */}
        <div className="shrink-0">
          {deleting ? (
            <Badge tone="neutral" spinning>
              Deleting
            </Badge>
          ) : (
            <EnvironmentBadge status={environment.status} runtime={envRuntime} />
          )}
        </div>
      </div>

      {/* Truncated, never wrapped: the derived address is the 24-character
          project id plus the name, and left to wrap it takes the row over. */}
      {deployed && environment.url ? (
        <a
          href={environment.url}
          target="_blank"
          rel="noopener noreferrer"
          title={environment.url}
          onClick={(e) => e.stopPropagation()}
          // `self-start` is load-bearing, not decoration. The row is a column
          // flex container, so `align-items: stretch` blockifies this anchor
          // and stretches it across the full row width regardless of
          // `inline-flex` — which made the whole line a link, and clicking to
          // the left or right of the address opened the app instead of the
          // environment. Shrunk to its content, the link is only where the
          // text is.
          className="inline-flex self-start items-center gap-bg-v2-xs text-border-brand hover:underline min-w-0 max-w-full"
        >
          {/* min-w-0 so it can still shrink now that the anchor is
              fit-content: a flex child defaults to min-width:auto, which
              refuses to go below its text and would defeat the truncation. */}
          <Text variant="text-xs" className="!text-border-brand truncate min-w-0">
            {address}
          </Text>
          <Icon name={IconName.ExternalLink} size="xs" inheritColor className="shrink-0" />
        </a>
      ) : (
        <Text variant="text-xs" color="tertiary" className="truncate max-w-full">
          {address ? `${address} · available once deployed` : 'URL available once deployed'}
        </Text>
      )}

      {/* When, not which commit: a hash identifies an environment exactly and
          tells you nothing about whether to act on it, while "2 hours ago" is
          the opposite, and this is a list you scan. The commit is in the
          details view, named and in full. */}
      <div className="flex items-center gap-bg-v2-xs min-w-0">
        {environment.deployed_at && (
          <>
            <Text variant="text-xs" color="tertiary" className="shrink-0">
              Deployed {getRelativeTime(environment.deployed_at)}
            </Text>
            <Text variant="text-xs" color="tertiary" className="shrink-0">
              ·
            </Text>
          </>
        )}
        {/* The span, not the Text, carries the clipping: it is the flex item,
            so it is blockified and `overflow` applies — and it is what the
            hook measures. The tooltip holds the sentence only when it is
            actually cut off, plus the fork, which has nowhere else to live. */}
        <span
          ref={positionRef}
          className="truncate min-w-0 flex-1"
          title={positionTitle}
        >
          <Text as="span" variant="text-xs" color={pipelineTone(environment)}>
            {summary || 'Never deployed'}
          </Text>
        </span>
      </div>
    </div>
  );
};

/**
 * One pipeline: a main and everything it feeds, inside a single border.
 *
 * The card *is* the grouping. Four equally-weighted cards with a connector
 * between them did not read as a hierarchy; one border around the set does.
 *
 * Collapsible only when there is something to collapse — a main with no subs
 * gets no chevron, because an affordance that does nothing is worse than none.
 */
/* The tray's left padding is not a guess: the main row's text starts at
   4 (card padding) + 24 (chevron column) + 4 (gap) + 12 (row padding) = 44px,
   and 32 + 12 lands a sub's text on exactly the same edge. Parent and child
   read down one line rather than stepping in or out. */
const TRAY =
  'bg-bg-secondary border-t border-border-secondary pl-bg-v2-4xl pr-bg-v2-xs py-bg-v2-md';

/** The environment a row promotes from — the one before it in the chain.
 *  Taken by position rather than looked up, because `chain` is already in
 *  promotion order and `below` is that list minus the head. */
const chainAbove = (indexBelow: number, group: EnvironmentGroup) => group.chain[indexBelow];


const PipelineCard: React.FC<RowHandlers & { group: EnvironmentGroup }> = ({ group, ...rest }) => {
  const [collapsed, setCollapsed] = useState(false);
  // Everything after the head, in promotion order.
  const below = group.chain.slice(1);
  const collapsible = below.length > 0;
  // Folding a pipeline away must not be able to hide the reason to open it.
  const hidden = collapsed ? below.filter(needsAttention).length : 0;

  return (
    <div
      className="bg-bg-primary border border-border-secondary box-border flex flex-col rounded-bg-v2-md w-full overflow-hidden"
      style={{
        boxShadow:
          '0px 1px 3px 0px var(--shadow-sm-01, rgba(10, 13, 18, 0.1)), 0px 1px 2px -1px var(--shadow-sm-02, rgba(10, 13, 18, 0.1))',
      }}
    >
      <div className="flex items-start gap-bg-v2-xs p-bg-v2-xs">
        {collapsible ? (
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            title={collapsed ? 'Show sub-environments' : 'Hide sub-environments'}
            aria-expanded={!collapsed}
            className="flex items-center justify-center w-[24px] h-[32px] shrink-0 rounded-bg-v2-sm hover:bg-bg-secondary transition-colors"
          >
            <Icon
              name={collapsed ? IconName.ChevronRight : IconName.ChevronDown}
              size="xs"
              className="text-text-tertiary"
              inheritColor
            />
          </button>
        ) : (
          // Keeps every main's name on the same left edge whether or not it
          // has subs, so a list of cards does not look ragged.
          <div className="w-[24px] shrink-0" aria-hidden />
        )}
        <div className="flex-1 min-w-0">
          <EnvironmentRow
            environment={group.head}
            trailing={
              collapsed ? (
                <>
                  <Text variant="text-xs" color="tertiary" className="shrink-0">
                    {below.length === 1 ? '1 environment' : `${below.length} environments`}
                  </Text>
                  {/* Folding a pipeline away must not be able to hide the
                      reason to open it. */}
                  {hidden > 0 && (
                    <Badge tone="warning">
                      {hidden === 1 ? '1 needs attention' : `${hidden} need attention`}
                    </Badge>
                  )}
                </>
              ) : undefined
            }
            {...rest}
          />
        </div>
      </div>

      {collapsible && !collapsed && (
        // A tinted tray, full-bleed to the card edges — the card's
        // overflow-hidden clips it to the rounded corner. The shade separates
        // the pipeline from the head that feeds it.
        <div className={TRAY}>
          {below.map((environment, index) => (
            <div key={environment.id} className="flex w-full gap-bg-v2-md">
              {/* The connector, and it earns its place now. In the hub model a
                  line down the side was wrong: it implied a sequence between
                  environments that were unordered siblings of one main. Here
                  the sequence is exactly what it means — each row promotes
                  from the one above it — so the line *is* the relationship
                  rather than decoration, and it replaces the horizontal
                  dividers that used to say "a set".

                  Dashed while the link cannot carry anything yet, because the
                  environment above has no version to promote. Red once this
                  one has diverged from it. */}
              <div className="relative w-[20px] shrink-0 flex justify-center">
                <div
                  aria-hidden
                  className={`absolute inset-y-0 border-l ${
                    environment.pipeline_state === 'diverged'
                      ? 'border-border-error-subtle'
                      : 'border-border-secondary'
                  } ${chainAbove(index, group)?.deployed_commit_hash ? '' : 'border-dashed'}`}
                />
                {/* Sits on the line, at the height of the name beside it. */}
                <div className="relative mt-bg-v2-md">
                  <ChainNode
                    status={environment.status}
                    runtime={rest.runtime[environment.id]}
                  />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <EnvironmentRow environment={environment} nested {...rest} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface EnvironmentListViewProps {
  loading: boolean;
  error: string | null;
  environments: ProjectEnvironment[];
  /** Live container state, keyed by environment id. */
  runtime: Record<string, EnvironmentRuntime>;
  /** Environment whose start/stop request is in flight. */
  runtimeActionId: string | null;
  /** Every id currently being deleted; several deletes may overlap. */
  deletingIds: string[];
  actionError: string | null;
  isBuilding: boolean;
  onStartStop: (environment: ProjectEnvironment, action: 'start' | 'stop') => void;
  onRetry: () => void;
  onEdit: (environment: ProjectEnvironment) => void;
  onDelete: (environment: ProjectEnvironment) => void;
  onOpenDetails: (environment: ProjectEnvironment) => void;
  onCreate: () => void;
  project: DevProject | null;
}

/** The environment list: create button, then one card per pipeline. */
export const EnvironmentListView: React.FC<EnvironmentListViewProps> = ({
  loading,
  error,
  environments,
  runtime,
  runtimeActionId,
  deletingIds,
  actionError,
  onStartStop,
  onRetry,
  onEdit,
  onDelete,
  onOpenDetails,
  onCreate,
  project,
}) => {
  const groups = useMemo(() => groupEnvironments(environments), [environments]);

  if (loading) {
    return (
      <div className="flex flex-col gap-bg-v2-lg w-full">
        {[0, 1].map((index) => (
          <div
            key={index}
            className="h-[104px] skeleton-shimmer rounded-bg-v2-md w-full animate-fade-in-up opacity-0"
            style={{ animationDelay: `${index * 0.08}s`, animationFillMode: 'forwards' }}
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-bg-v2-8xl gap-bg-v2-lg">
        <Icon name={IconName.Alert} size="xxl" className="text-text-error-primary" inheritColor />
        <Text variant="text-md" weight="semibold" color="primary">
          Error Loading Environments
        </Text>
        <Text variant="text-sm" color="secondary" align="center">
          {error}
        </Text>
        <button
          onClick={onRetry}
          className="mt-bg-v2-md px-bg-v2-xl py-bg-v2-md bg-bg-brand-solid text-text-primary-on-brand rounded-bg-v2-md hover:bg-bg-brand-solid-hover transition-colors"
        >
          <Text variant="text-sm" weight="medium" color="on-brand">
            Try Again
          </Text>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-bg-v2-lg w-full">
      <IfHasPermission permissions={project?.permissions} requires={Permissions.UpdateProjectPermission}>
        <button
          onClick={onCreate}
          className="w-full flex items-center justify-center gap-bg-v2-xs px-bg-v2-lg py-bg-v2-md bg-bg-brand-solid text-text-primary-on-brand rounded-bg-v2-md hover:bg-bg-brand-solid-hover transition-colors"
        >
          <Icon name={IconName.Plus} size="sm" className="text-text-primary-on-brand" inheritColor />
          <Text variant="text-sm" weight="medium" color="on-brand">
            Create environment
          </Text>
        </button>
      </IfHasPermission>

      {actionError && (
        <div className="flex items-center gap-bg-v2-xs p-bg-v2-md bg-bg-error-primary border border-border-error-subtle rounded-bg-v2-sm">
          <Icon name={IconName.Alert} size="sm" className="text-text-error-primary shrink-0" inheritColor />
          <Text variant="text-xs" color="error">
            {actionError}
          </Text>
        </div>
      )}

      {groups.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-bg-v2-8xl gap-bg-v2-lg text-center w-full">
          <Icon name={IconName.Environment} size="xxl" className="text-text-quaternary" inheritColor />
          <Text variant="text-md" weight="semibold" color="primary" align="center">
            No Environments
          </Text>
          <Text variant="text-sm" color="secondary" align="center" className="max-w-[300px]">
            Create an environment to test this project on its own URL without touching the live app.
          </Text>
        </div>
      ) : (
        groups.map((group, index) => (
          <div
            key={group.head.id}
            className="animate-fade-in-up opacity-0 w-full"
            style={{ animationDelay: `${index * 0.08}s`, animationFillMode: 'forwards' }}
          >
            <PipelineCard
              group={group}
              runtime={runtime}
              runtimeActionId={runtimeActionId}
              deletingIds={deletingIds}
              onStartStop={onStartStop}
              onEdit={onEdit}
              onDelete={onDelete}
              onOpenDetails={onOpenDetails}
              project={project}
              all={environments}
            />
          </div>
        ))
      )}
    </div>
  );
};

export default EnvironmentListView;
